import React, { useState } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SectionContainer from './SectionContainer';
import SectionEyebrow from './SectionEyebrow';

const PlatformChip = ({ Icon, label }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 0.6,
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.25)',
      color: 'rgba(255,255,255,0.85)',
    }}
  >
    <Icon sx={{ fontSize: 16 }} />
    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{label}</Typography>
  </Box>
);

const MobileAppSection = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return;
    setSubmitted(true);
    setEmail('');
  };

  return (
    <SectionContainer sx={{ py: { xs: 6, md: 10 } }}>
      <Box
        sx={(theme) => ({
          borderRadius: 5,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: { xs: 5, md: 8 },
          px: { xs: 3, sm: 5, md: 8 },
          py: { xs: 6, sm: 7, md: 8 },
          background:
            theme.palette.mode === 'dark'
              ? 'radial-gradient(90% 120% at 85% 20%, rgba(37,99,235,0.32) 0%, rgba(8,11,20,0) 55%), linear-gradient(180deg, #14161d 0%, #05060a 100%)'
              : 'linear-gradient(135deg, #0F1B3D 0%, #1E3A8A 100%)',
        })}
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          <SectionEyebrow color="#F59E0B" textColor="rgba(255,255,255,0.85)">Mobile App</SectionEyebrow>
          <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: { xs: 24, md: 32 }, color: '#fff' }}>
            Take Remembr with you
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: { xs: 14, md: 15.5 }, maxWidth: 440, mb: 3 }}>
            Our iOS and Android apps are in the works. Drop your email and we'll let you know the moment they're
            ready to download.
          </Typography>

          {submitted ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.08)',
                maxWidth: 420,
                mb: 3,
              }}
            >
              <NotificationsActiveOutlinedIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
              <Typography sx={{ color: '#fff', fontSize: 13.5, fontWeight: 600 }}>
                Thanks! We'll email you as soon as we launch.
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, maxWidth: 420, mb: 3 }}
            >
              <TextField
                type="email"
                required
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    borderRadius: 999,
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#fff' },
                  },
                  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 600,
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Notify me
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <PlatformChip Icon={AppleIcon} label="iOS · launching soon" />
            <PlatformChip Icon={AndroidIcon} label="Android · launching soon" />
          </Box>
        </Box>

        <Box
          sx={{
            flex: '0 0 auto',
            width: { xs: '100%', md: 320 },
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 220,
              aspectRatio: '487 / 1062',
              borderRadius: 5,
              overflow: 'hidden',
              boxShadow: '0 30px 70px rgba(0,0,0,0.45)',
              border: '6px solid rgba(255,255,255,0.08)',
              transform: { md: 'rotate(-2deg)' },
            }}
          >
            <Box
              component="img"
              src="/mobile-screen-landing-page.png"
              alt="Remembr mobile app preview"
              sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
            />
          </Box>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default MobileAppSection;
